import {Dropdown as PepsicoDsDropdown} from '@pepsico-ds/ui';
import React, {useCallback, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
/**
 * Dropdown component that renders a dropdown menu with various customization options.
 *
 * @param {Object} props - The properties object.
 * @param {any} props.value - The current value of the dropdown.
 * @param {Function} props.updateValue - Function to update the value of the dropdown.
 * @param {string} [props.label=''] - The label for the dropdown.
 * @param {Object} props.schema - The schema object containing options and other configurations.
 * @param {Object} props.uiSchema - The UI schema object containing UI-specific configurations.
 * @param {boolean} [props.required=false] - Indicates if the dropdown is required.
 * @param {boolean} [props.isError=false] - Indicates if there is an error.
 * @param {boolean} [props.isJsonWrap=true] - Determines if the dropdown should use JSON wrapping.
 * @param {Array} [props.childList=[]] - List of child elements for the dropdown.
 * @param {string} [props.dropdownHeader=''] - The header for the dropdown.
 * @param {string} [props.className=''] - Additional class names for the dropdown.
 * @param {boolean} [props.isDark=true] - Indicates if the dropdown should use a dark theme.
 * @param {('single'|'multiple')} [props.selection='single'] - The selection mode for the dropdown.
 * @param {('small'|'medium')} [props.size='medium'] - The size of the dropdown.
 * @param {string} [props.placeholder=''] - The placeholder text for the dropdown.
 * @param {string} [props.helperText=''] - The helper text for the dropdown.
 * @param {string} [props.errorMessage=''] - The error message for the dropdown.
 * @param {boolean} [props.preventTyping=false] - Whether to prevent typing in the dropdown (selection-only mode).
 * @param {boolean} [props.disabled=false] - Whether to disable the dropdown.
 * @param {boolean} [props.isClearable=true] - Whether to allow clearing the dropdown selection.
 * @returns {JSX.Element} The rendered Dropdown component.
 */
const Dropdown = ({
  value,
  updateValue,
  label = '',
  schema,
  uiSchema,
  required = false,
  isError = false,
  isJsonWrap = true,
  childList = [],
  dropdownHeader = '',
  className = '',
  isDark = true,
  selection = 'single',
  size = 'medium',
  placeholder = '',
  helperText = '',
  errorMessage = '',
  isClearable = true,
  hideMenuListOnSelection = true,
  preventTyping = false,
  disabled = false,
  style = {},
  menuPosition = 'bottom-left',
  showLabelIcon
}) => {
  // Use a ref to always call the latest updateValue, avoiding stale closures
  // when PepsicoDsDropdown caches the setSelectedValue callback internally.
  const updateValueRef = useRef(updateValue);
  updateValueRef.current = updateValue;

  const newValue = useCallback((selectedValue) => {
    // `pepsico-ds` emits falsy values (e.g. `null` or `[]`) when the selection is cleared.
    // We must propagate those changes so JSONForms/state can clear the underlying field.
    if (typeof updateValueRef.current === 'function') {
      updateValueRef.current(selectedValue);
    }
  }, []);

  const dropdownId = useMemo(
    () => `dropdown-${label.replace(/\s+/g, '-').toLowerCase()}`,
    [label]
  );

  const getIsError = useCallback(() => {
    return !uiSchema || uiSchema.value === '' || isError;
  }, [uiSchema, isError]);

  const getHelperText = useCallback(() => {
    const isError = getIsError();
    if (isError) {
      return uiSchema?.errorMessage;
    }
    return helperText;
  }, [getIsError, uiSchema?.errorMessage, helperText]);

  // Unified props based on isJsonWrap
  const dropdownProps = useMemo(() => {
    const hideFeedbackIconClass = uiSchema?.hideFeedbackIcon
      ? ' hide-feedback-icon'
      : '';
    return isJsonWrap
      ? {
          childList: schema?.options || [],
          dropdownHeader: schema?.header,
          className: `${uiSchema?.className || ''}${hideFeedbackIconClass}`,
          isDark: uiSchema?.isDark ?? '',
          error: getIsError(),
          helperText: getHelperText(),
          selection: uiSchema?.selection || 'single',
          size: uiSchema?.size || 'medium',
          placeholder: uiSchema?.placeholder || '',
          disabled: uiSchema?.disabled || false,
          isClearable: uiSchema?.isClearable || true
        }
      : {
          childList,
          dropdownHeader,
          className: `${className || ''}${hideFeedbackIconClass}`,
          isDark: isDark ?? '',
          error: isError,
          helperText: isError ? errorMessage : helperText,
          selection,
          size,
          placeholder,
          isClearable: isClearable,
          hideMenuListOnSelection: hideMenuListOnSelection,
          menuPosition: menuPosition
        };
  }, [
    isJsonWrap,
    schema,
    uiSchema,
    childList,
    dropdownHeader,
    className,
    isDark,
    isError,
    errorMessage,
    helperText,
    selection,
    size,
    placeholder,
    isClearable,
    hideMenuListOnSelection,
    menuPosition,
    getIsError,
    getHelperText
  ]);

  return (
    <PepsicoDsDropdown
      id={dropdownId}
      style={{minWidth: '0px', ...style}}
      isRequired={required}
      label={label}
      selectedValue={value || ''}
      setSelectedValue={disabled ? () => {} : newValue}
      autoComplete='off'
      disabled={disabled}
      isClearable={isClearable}
      {...(preventTyping && {
        onKeyDown: (e) => {
          e.preventDefault();
        }
      })}
      {...dropdownProps}
      {...(showLabelIcon !== undefined && {showLabelIcon})}
      data-testid='dropdown-atom'
    />
  );
};

Dropdown.propTypes = {
  value: PropTypes.any,
  updateValue: PropTypes.func,
  label: PropTypes.string,
  schema: PropTypes.shape({
    options: PropTypes.array,
    header: PropTypes.string
  }),
  uiSchema: PropTypes.shape({
    value: PropTypes.string,
    errorMessage: PropTypes.string,
    className: PropTypes.string,
    isDark: PropTypes.bool,
    selection: PropTypes.oneOf(['single', 'multiple']),
    size: PropTypes.oneOf(['small', 'medium']),
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    isClearable: PropTypes.bool,
    hideFeedbackIcon: PropTypes.bool
  }),
  required: PropTypes.bool,
  isError: PropTypes.bool,
  isJsonWrap: PropTypes.bool,
  childList: PropTypes.array,
  dropdownHeader: PropTypes.string,
  className: PropTypes.string,
  isDark: PropTypes.bool,
  selection: PropTypes.oneOf(['single', 'multiple']),
  size: PropTypes.oneOf(['small', 'medium']),
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  errorMessage: PropTypes.string,
  preventTyping: PropTypes.bool,
  disabled: PropTypes.bool,
  isClearable: PropTypes.bool,
  hideMenuListOnSelection: PropTypes.bool,
  style: PropTypes.object,
  menuPosition: PropTypes.string
};

export default React.memo(Dropdown);
